import { ControlledField } from '@shared/components';
import { USER_GROUPS } from '@modules/users';
import { TCustomGroup } from '@modules/users/types/organizations.types';
import { Control, FieldErrors, FieldPath, FieldValues } from 'react-hook-form';

import { ApprovalStatus, TApprovalPhaseKey } from '../types';
import { TFunctionRow } from '../helpers/functionRows.helper';
import FunctionUserSelect from './FunctionUserSelect';

type TApprovalUserFormValues = {
  authors?: string[];
  reviewers?: string[];
  approvers?: string[];
};

type TProps<T extends FieldValues & TApprovalUserFormValues> = {
  phase: TApprovalPhaseKey;
  functionsEnabled?: boolean;
  control: Control<T>;
  errors: FieldErrors<T>;
  rows: TFunctionRow[];
  onRowsChange: (rows: TFunctionRow[]) => void;
  customGroups: TCustomGroup[];
  required?: boolean;
  readOnly?: boolean;
  signedUserIds?: string[];
};

const PHASE_FIELD_CONFIG: Record<
  TApprovalPhaseKey,
  { name: keyof TApprovalUserFormValues; label: string; group: USER_GROUPS }
> = {
  [ApprovalStatus.AUTHORING]: {
    name: 'authors',
    label: 'Select authors',
    group: USER_GROUPS.AUTHOR,
  },
  [ApprovalStatus.REVIEWING]: {
    name: 'reviewers',
    label: 'Select reviewers',
    group: USER_GROUPS.REVIEWER,
  },
  [ApprovalStatus.APPROVING]: {
    name: 'approvers',
    label: 'Select approvers',
    group: USER_GROUPS.APPROVER,
  },
};

const ApprovalPhaseUserField = <T extends FieldValues & TApprovalUserFormValues>({
  phase,
  functionsEnabled,
  control,
  errors,
  rows,
  onRowsChange,
  customGroups,
  required,
  readOnly,
  signedUserIds,
}: TProps<T>) => {
  const { name, label, group } = PHASE_FIELD_CONFIG[phase];
  const fieldName = name as FieldPath<T>;

  return functionsEnabled ? (
    <FunctionUserSelect
      label={label}
      rows={rows}
      onChange={onRowsChange}
      customGroups={customGroups}
      group={group}
      required={required}
      disabled={readOnly}
      signedUserIds={signedUserIds}
    />
  ) : (
    <ControlledField
      name={fieldName}
      control={control}
      errors={errors}
      label={label}
      inputType="multiuserselect"
      group={group}
      required={required}
      readonly={readOnly}
      lockedUserIds={signedUserIds}
      lockedUserMessage="This user has signed and cannot be removed."
    />
  );
};

export default ApprovalPhaseUserField;
